import { useEffect, useState } from 'react';
import { CreditCard, ExternalLink, KeyRound, Link2, Trash } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import type { WompiAmbiente } from '@/types/integrations';
import {
  useRemoveWompiConfig,
  useSaveWompiConfig,
  useTestWompiConnection,
  useWompiStatus,
} from './use-wompi';

/**
 * Pago en línea con Wompi (docs/WOMPI.md) — un método de pago ADICIONAL.
 *
 * Por qué va en «Integraciones» y no en «Métodos de pago»: lo que se guarda aquí son
 * CREDENCIALES de la cuenta de comercio (llaves + secreto de eventos), como las de los canales o
 * las API keys. Los métodos de pago que el bot le dicta al cliente se siguen configurando en
 * Ajustes → Métodos de pago y NO se tocan: con Wompi conectado el bot ofrece los de siempre Y
 * además la opción de pagar en línea.
 *
 * Contrato del backend que esta tarjeta respeta:
 *  - la privada y el secreto NUNCA se devuelven (solo máscaras) y guardar hace MERGE, así que para
 *    apagar/encender o cambiar el ambiente no hay que reescribirlas;
 *  - «Probar conexión» va contra la API real de Wompi (`GET /v1/merchants/{llave pública}`) y
 *    siempre responde 200: el veredicto va en `ok`, no en el código HTTP.
 */
export function WompiCard() {
  const { tenantId, canEdit } = useActiveTenantAccess();
  const status = useWompiStatus(tenantId);
  const guardar = useSaveWompiConfig(tenantId);
  const quitar = useRemoveWompiConfig(tenantId);
  const probar = useTestWompiConnection(tenantId);

  const [ambiente, setAmbiente] = useState<WompiAmbiente>('sandbox');
  const [activo, setActivo] = useState(true);
  const [llavePublica, setLlavePublica] = useState('');
  const [llavePrivada, setLlavePrivada] = useState('');
  const [secreto, setSecreto] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);

  // El estado llega del backend una sola vez (sin pisar lo que el usuario esté escribiendo).
  useEffect(() => {
    if (!status.data) return;
    setAmbiente((a) => status.data?.ambiente ?? a);
    setActivo((a) => (status.data ? status.data.activo : a));
    setLlavePublica((a) => a || status.data?.llavePublica || '');
  }, [status.data]);

  const configurada = Boolean(status.data?.configurada);
  const hayLlavesNuevas =
    llavePublica.trim().length > 0 || llavePrivada.trim().length > 0 || secreto.trim().length > 0;

  const errorGuardar = guardar.isError ? getErrorMessage(guardar.error) : null;
  const errorProbar = probar.isError ? getErrorMessage(probar.error) : null;

  /** Guarda: si ya hay llaves, solo lo que el admin escribió + ambiente/activo (el backend mergea). */
  async function handleGuardar() {
    if (guardar.isPending) return;
    guardar.reset();

    await guardar.mutateAsync({
      ambiente,
      activo,
      ...(llavePublica.trim() ? { llavePublica: llavePublica.trim() } : {}),
      ...(llavePrivada.trim() ? { llavePrivada: llavePrivada.trim() } : {}),
      ...(secreto.trim() ? { secretoEventos: secreto.trim() } : {}),
    });

    // Las llaves se van de los campos (el backend ya las tiene): no se dejan a la vista.
    setLlavePrivada('');
    setSecreto('');
  }

  /** Apaga/enciende sin perder las llaves (merge del backend). */
  async function handleActivar(valor: boolean) {
    setActivo(valor);
    await guardar.mutateAsync({ activo: valor });
  }

  async function handleProbar() {
    if (probar.isPending) return;
    probar.reset();
    await probar.mutateAsync({
      ambiente,
      ...(llavePublica.trim() ? { llavePublica: llavePublica.trim() } : {}),
    });
  }

  async function handleQuitar() {
    if (quitar.isPending) return;
    try {
      await quitar.mutateAsync();
      setConfirmRemove(false);
      setLlavePublica('');
      setActivo(true);
    } catch {
      // El error se pinta abajo (el ConfirmDialog lo recibe por prop).
    }
  }

  const tonoBadge = configurada ? (activo ? 'success' : 'warning') : 'neutral';

return (
    <>
      <Card>
        <CardHeader
          actions={
            <Badge tone={tonoBadge}>
              {configurada ? (activo ? 'Conectado' : 'Apagado') : 'Sin conectar'}
            </Badge>
          }
        >
          <CardTitle>Pago en línea con Wompi (opcional)</CardTitle>
          <CardDescription>
            Permite que el bot ofrezca pagar con <strong>tarjeta, Nequi o PSE</strong> en el mismo
            chat: cuando el cliente elige esa opción, el sistema le manda un enlace de pago por el
            monto EXACTO de su pedido y la venta se confirma sola cuando la pasarela avisa que el
            pago pasó. Tus <strong>métodos de pago de siempre no cambian</strong>: el bot los sigue
            ofreciendo igual (Nequi, transferencia, contraentrega…). Se conecta con la cuenta de{' '}
            <a
              href="https://comercios.wompi.co"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              comercios.wompi.co
            </a>
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {status.data?.configurada ? (
            <div className="flex items-start justify-between gap-4 rounded-panel border border-line bg-surface-muted p-4">
              <div className="min-w-0">
                <p className="text-body font-medium text-ink">
                  Wompi {status.data.ambiente === 'sandbox' ? 'en pruebas' : 'en producción'}
                </p>
                <p className="mt-0.5 text-caption text-ink-muted">
                  Privada <span className="font-mono">{status.data.previewPrivada ?? '••••'}</span> ·
                  Secreto <span className="font-mono">{status.data.previewSecreto ?? '••••'}</span>{' '}
                  <span className="text-ink-faint">(el backend nunca las devuelve completas)</span>
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Switch
                  checked={status.data.activo}
                  onChange={(valor) => void handleActivar(valor)}
                  disabled={!canEdit || guardar.isPending}
                  label="Activar el pago en línea"
                />
                <span className="text-caption text-ink-muted">
                  {status.data.activo ? 'Activo' : 'Apagado'}
                </span>
              </div>
            </div>
          ) : null}

          {status.data?.webhookUrl ? (
            <div className="rounded-panel border border-line bg-surface-muted p-4">
              <p className="text-body font-medium text-ink">Paso final: avisos de pago</p>
              <p className="mt-1 text-caption text-ink-muted">
                En tu dashboard de Wompi → <strong>Eventos</strong>, pon esta URL como{' '}
                <em>URL de eventos</em> (una por ambiente: pruebas y producción). Con eso, cuando el
                cliente pague, el sistema confirma el pedido solo.
              </p>
              <p className="mt-2 break-all rounded-control bg-surface px-3 py-2 font-mono text-caption text-ink">
                {status.data.webhookUrl}
              </p>
            </div>
          ) : (
            <Alert tone="warning">
              El backend no tiene configurada <code>PUBLIC_BASE_URL</code>, así que no se puede
              mostrar la URL de eventos que Wompi necesita para avisar los pagos. Sin eso el pedido se
              registra, pero nadie lo confirma solo.
            </Alert>
          )}

<Field
            label="Ambiente"
            htmlFor="wompiAmbiente"
            hint="Empieza por Pruebas (sandbox): no se mueve dinero real y se puede verificar el flujo completo."
          >
            <Select
              id="wompiAmbiente"
              value={ambiente}
              disabled={!canEdit || guardar.isPending}
              onChange={(evento) => setAmbiente(evento.target.value as WompiAmbiente)}
            >
              <option value="sandbox">Pruebas (sandbox) — llaves pub_test_ / prv_test_</option>
              <option value="produccion">Producción — llaves pub_prod_ / prv_prod_</option>
            </Select>
          </Field>

          <Field
            label="Llave pública"
            htmlFor="wompiPublica"
            hint={
              ambiente === 'sandbox'
                ? 'Empieza por pub_test_… (Comercios → Llaves). No es secreta y se muestra guardada.'
                : 'En producción empieza por pub_prod_…'
            }
          >
            <Input
              id="wompiPublica"
              name="llavePublica"
              placeholder={ambiente === 'sandbox' ? 'pub_test_…' : 'pub_prod_…'}
              disabled={!canEdit || guardar.isPending}
              value={llavePublica}
              onChange={(evento) => setLlavePublica(evento.target.value)}
            />
          </Field>

          <Field
            label="Llave privada"
            htmlFor="wompiPrivada"
            hint="Se usa para crear los enlaces de pago. Se guarda cifrada y nunca se vuelve a mostrar: déjala vacía para conservarla."
          >
            <PasswordInput
              id="wompiPrivada"
              name="llavePrivada"
              autoComplete="off"
              placeholder={ambiente === 'sandbox' ? 'prv_test_…' : 'prv_prod_…'}
              leftIcon={<KeyRound size={16} />}
              disabled={!canEdit || guardar.isPending}
              value={llavePrivada}
              onChange={(evento) => setLlavePrivada(evento.target.value)}
            />
          </Field>

          <Field
            label="Secreto de eventos"
            htmlFor="wompiSecreto"
            hint="Es el que Wompi usa para firmar los avisos de pago (distinto de la llave privada). Se guarda cifrado."
          >
            <PasswordInput
              id="wompiSecreto"
              name="secretoEventos"
              autoComplete="off"
              placeholder={ambiente === 'sandbox' ? 'test_events_…' : 'prod_events_…'}
              leftIcon={<Link2 size={16} />}
              disabled={!canEdit || guardar.isPending}
              value={secreto}
              onChange={(evento) => setSecreto(evento.target.value)}
            />
          </Field>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              leftIcon={<CreditCard size={16} />}
              loading={guardar.isPending}
              disabled={!canEdit || (!hayLlavesNuevas && configurada)}
              onClick={() => void handleGuardar()}
            >
              {configurada ? 'Guardar cambios' : 'Guardar y conectar'}
            </Button>

            <Button
              variant="secondary"
              loading={probar.isPending}
              disabled={!canEdit || (!llavePublica.trim() && !configurada)}
              onClick={() => void handleProbar()}
            >
              Probar conexión
            </Button>

            {configurada ? (
              <Button
                variant="ghost"
                className="text-danger-strong hover:bg-danger-soft"
                leftIcon={<Trash size={16} />}
                disabled={!canEdit || guardar.isPending}
                onClick={() => setConfirmRemove(true)}
              >
                Desconectar
              </Button>
            ) : null}

            {!canEdit ? (
              <span className="text-caption text-ink-muted">
                Solo un administrador del negocio puede configurar esto.
              </span>
            ) : null}
          </div>

          {errorGuardar ? <Alert tone="warning">{errorGuardar}</Alert> : null}
          {errorProbar ? <Alert tone="warning">{errorProbar}</Alert> : null}
          {probar.data ? (
            <Alert tone={probar.data.ok ? 'success' : 'warning'}>{probar.data.message}</Alert>
          ) : null}
          {quitar.isError ? <Alert tone="warning">{getErrorMessage(quitar.error)}</Alert> : null}

          <p className="text-caption text-ink-faint">
            <a
              href="https://docs.wompi.co/docs/colombia/inicio-rapido/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-medium text-brand-700 hover:underline"
            >
              Ver la guía de Wompi
              <ExternalLink size={13} />
            </a>
          </p>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={() => void handleQuitar()}
        title="Desconectar Wompi"
        description="El bot dejará de ofrecer pago en línea y se borrarán las llaves guardadas. Los pedidos que ya se pagaron por Wompi no se tocan. Tus métodos de pago de siempre siguen funcionando."
        confirmLabel="Desconectar"
        loading={quitar.isPending}
        error={quitar.isError ? getErrorMessage(quitar.error) : null}
      />
    </>
  );
}